// ============================================================
// historialPage.js — Página "Historial"
// ------------------------------------------------------------
// - Filtra por tipo (todo / imagen / vídeo / avatar / plantilla)
// - Búsqueda por texto en el título
// - Expone window.Vimo.history para que el backend inyecte
//   datos reales:
//     window.Vimo.history.setItems(items)
//     window.Vimo.history.addItem(item)
//     window.Vimo.history.clear()
//     window.Vimo.history.fetch()    // pide al backend
//
// Estructura de un item:
// {
//   id:        'img_a1b2c3',                // identificador único
//   type:      'image' | 'video' | 'avatar' | 'template',
//   title:     'Texto descriptivo del prompt o nombre de sesión',
//   thumb:     'https://...',               // url miniatura (opcional)
//   status:    'completed' | 'processing' | 'pending' | 'failed',
//   createdAt: '2026-05-25T14:32:00Z',      // ISO 8601
//   meta:      'Kling 2.1 · 1080p',         // texto extra opcional
//   resultsCount: 3,                        // opcional
//   url:       'imagenes.html?session=img_a1b2c3'  // opcional; si falta se autogenera
// }
// ============================================================
import { qs, qsa, on, debounce, esc } from './utils.js';
import { listHistory } from './api.js';

// Mapa de configuración por tipo
const TYPE_CONFIG = {
  image:    { label: 'Imagen',    icon: 'bi-image',          page: 'imagenes.html' },
  video:    { label: 'Vídeo',     icon: 'bi-play-btn',       page: 'video.html' },
  avatar:   { label: 'Avatar',    icon: 'bi-person-circle',  page: 'avatar.html' },
  template: { label: 'Plantilla', icon: 'bi-code-square',    page: 'plantillas.html' },
};

const STATUS_CONFIG = {
  completed:  { label: 'Completado', cls: 'history-status-ok',      icon: 'bi-check-circle-fill' },
  processing: { label: 'Procesando', cls: 'history-status-pending', icon: null /* spinner */ },
  pending:    { label: 'En cola',    cls: 'history-status-pending', icon: null },
  queued:     { label: 'En cola',    cls: 'history-status-pending', icon: null },
  failed:     { label: 'Falló',      cls: 'history-status-fail',    icon: 'bi-x-circle-fill' },
};

// Estado interno
let _items = [];
let _activeType = 'all';
let _query = '';
let _currentPage = 1;
const PAGE_SIZE = 10;

// Paginación gestionada por servidor (opcional).
// Si el backend devuelve más páginas vía fetch, las setea aquí
// y la UI muestra los controles sin paginar localmente.
let _serverPagination = null;   // { page, totalPages, totalItems } | null

export function initHistorialPage() {
  if (document.body.dataset.page !== 'historial') return;

  // 1. Filtrado por tabs de tipo (reinicia a página 1)
  on(document, 'click', '.historial-type-tab', (e, tab) => {
    qsa('.historial-type-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    _activeType = tab.dataset.type;
    _currentPage = 1;
    applyFilters();
  });

  // 2. Búsqueda con debounce (reinicia a página 1)
  const search = qs('#historialSearch');
  search?.addEventListener('input', debounce((e) => {
    _query = e.target.value.trim().toLowerCase();
    _currentPage = 1;
    applyFilters();
  }, 200));

  // 3. Paginación: prev / next / nº de página
  on(document, 'click', '[data-page-action="prev"]', () => goToPage(_currentPage - 1));
  on(document, 'click', '[data-page-action="next"]', () => goToPage(_currentPage + 1));
  on(document, 'click', '#historialPagination [data-page]', (e, btn) => goToPage(Number(btn.dataset.page)));

  // 4. Estado inicial limpio (el backend inyecta los elementos reales)
  _items = [];

  // 5. API pública para el backend
  window.Vimo = window.Vimo || {};
  window.Vimo.history = {
    setItems,
    addItem,
    removeItem,
    clear,
    fetch:   fetchFromBackend,
    goToPage,
    setServerPagination,
    getItems: () => [..._items],
    pageSize: PAGE_SIZE,
  };

  // Cargar de forma automática el historial real del backend
  fetchFromBackend();
}

// ── Paginación ───────────────────────────────────────────────
function goToPage(n) {
  // Si el backend gestiona la paginación, le delegamos
  if (_serverPagination && typeof _serverPagination.onPageChange === 'function') {
    const total = _serverPagination.totalPages || 1;
    if (n < 1 || n > total) return;
    _serverPagination.page = n;
    _serverPagination.onPageChange(n);
    renderPagination(getFilteredItems().length);
    return;
  }

  const filtered = getFilteredItems();
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  n = Math.min(Math.max(1, n), totalPages);
  if (n === _currentPage) return;
  _currentPage = n;
  applyFilters();
  // Scroll al inicio de la lista
  qs('#historialList')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// Permite al backend tomar el control de la paginación
function setServerPagination(cfg) {
  // cfg = { page, totalPages, totalItems, onPageChange(page) }
  _serverPagination = cfg;
  if (cfg) _currentPage = cfg.page || 1;
  applyFilters();
}

// ── Lectura inicial desde el DOM (placeholders) ──────────────
function readItemsFromDOM() {
  return qsa('.history-card').map(card => ({
    id:        card.dataset.sessionId,
    type:      card.dataset.type,
    status:    card.dataset.status,
    title:     qs('.history-title', card)?.textContent.trim() || '',
    url:       card.getAttribute('href') || '',
    _node:     card,    // referencia al nodo DOM ya pintado
  }));
}

// ── Helpers de filtrado ──────────────────────────────────────
function getFilteredItems() {
  return _items.filter(item => {
    const matchesType  = _activeType === 'all' || item.type === _activeType;
    const matchesQuery = !_query || (item.title || '').toLowerCase().includes(_query);
    return matchesType && matchesQuery;
  });
}

function applyFilters() {
  const filtered = getFilteredItems();
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  if (_currentPage > totalPages) _currentPage = totalPages;

  // Cuando el backend pagina, todos los items que tenemos son la página actual
  const useLocalPaging = !_serverPagination;
  const start = useLocalPaging ? (_currentPage - 1) * PAGE_SIZE : 0;
  const end   = useLocalPaging ? start + PAGE_SIZE : filtered.length;
  const pageItems = filtered.slice(start, end);
  const pageItemIds = new Set(pageItems.map(i => i.id));

  // Mostrar solo los items que caen en la página actual
  _items.forEach(item => {
    item._node?.classList.toggle('is-hidden', !pageItemIds.has(item.id));
  });

  qs('#historialEmpty')?.classList.toggle('is-hidden', filtered.length > 0);
  renderPagination(filtered.length);
}

// ── Render de los controles de paginación ────────────────────
function renderPagination(totalFiltered) {
  const nav     = qs('#historialPagination');
  const summary = qs('#historialSummary');
  if (!nav) return;

  const totalPages = _serverPagination?.totalPages
    || Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const currentPage = _serverPagination?.page || _currentPage;

  // Si solo hay una página, ocultamos los controles
  if (totalPages <= 1) {
    nav.classList.add('is-hidden');
    if (summary) summary.classList.add('is-hidden');
    return;
  }
  nav.classList.remove('is-hidden');

  // Botones prev/next
  const prevBtn = qs('[data-page-action="prev"]', nav);
  const nextBtn = qs('[data-page-action="next"]', nav);
  if (prevBtn) prevBtn.disabled = currentPage <= 1;
  if (nextBtn) nextBtn.disabled = currentPage >= totalPages;

  // Números con elipsis (e.g. 1 … 4 [5] 6 … 12)
  const pagesEl = qs('#paginationPages');
  if (pagesEl) {
    pagesEl.innerHTML = buildPageButtons(currentPage, totalPages);
  }

  // Resumen "Mostrando 1-10 de 47"
  if (summary) {
    const startIdx = (currentPage - 1) * PAGE_SIZE + 1;
    const endIdx   = Math.min(currentPage * PAGE_SIZE, _serverPagination?.totalItems || totalFiltered);
    const totalIdx = _serverPagination?.totalItems || totalFiltered;
    summary.textContent = `Mostrando ${startIdx}–${endIdx} de ${totalIdx} sesiones`;
    summary.classList.remove('is-hidden');
  }
}

// Construye los números visibles con elipsis (estilo "1 … 4 5 6 … 12")
function buildPageButtons(current, total) {
  const pages = [];
  const add = (n) => pages.push(`<button class="pagination-page ${n === current ? 'is-active' : ''}" type="button" data-page="${n}">${n}</button>`);
  const dots = () => pages.push(`<span class="pagination-ellipsis">…</span>`);

  if (total <= 7) {
    for (let i = 1; i <= total; i++) add(i);
  } else {
    add(1);
    if (current > 3) dots();
    const start = Math.max(2, current - 1);
    const end   = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) add(i);
    if (current < total - 2) dots();
    add(total);
  }
  return pages.join('');
}

// ── Render dinámico ──────────────────────────────────────────
function setItems(items) {
  const list = qs('#historialList');
  if (!list) return;
  list.innerHTML = '';

  if (!items || items.length === 0) {
    _items = [];
    qs('#historialEmpty')?.classList.remove('is-hidden');
    renderPagination(0);
    return;
  }

  qs('#historialEmpty')?.classList.add('is-hidden');

  _items = items.map(item => {
    const node = buildCard(item);
    list.appendChild(node);
    return { ...item, _node: node };
  });
  _currentPage = 1;
  applyFilters();
}

function addItem(item) {
  const list = qs('#historialList');
  if (!list) return;
  const node = buildCard(item);
  list.prepend(node);
  _items.unshift({ ...item, _node: node });
  applyFilters();
}

function removeItem(id) {
  const idx = _items.findIndex(i => i.id === id);
  if (idx === -1) return;
  _items[idx]._node?.remove();
  _items.splice(idx, 1);
  applyFilters();
}

function clear() {
  qs('#historialList')?.replaceChildren();
  _items = [];
  applyFilters();
}

// Construye una <a class="history-card"> desde un item
function buildCard(item) {
  const cfg    = TYPE_CONFIG[item.type] || TYPE_CONFIG.image;
  const status = STATUS_CONFIG[item.status] || STATUS_CONFIG.completed;
  const url    = item.url || `${cfg.page}?session=${encodeURIComponent(item.id)}`;
  const isVideo = item.type === 'video' || item.type === 'template';

  const a = document.createElement('a');
  a.className = 'history-card';
  a.href = url;
  a.dataset.type = item.type;
  a.dataset.sessionId = item.id;
  a.dataset.status = item.status;

  // Miniatura
  let thumbHtml;
  const thumbUrl = item.thumb || item.url || (item.meta ? (item.meta.photoUrl || item.meta.photo) : null);
  if (item.status === 'failed') {
    thumbHtml = `<span class="history-thumb history-thumb-error"><i class="bi bi-exclamation-triangle"></i></span>`;
  } else if ((item.status === 'pending' || item.status === 'processing' || item.status === 'queued') && !thumbUrl) {
    thumbHtml = `<span class="history-thumb history-thumb-pending"><span class="history-spinner-lg"></span></span>`;
  } else {
    thumbHtml = `
      <span class="history-thumb">
        <img src="${esc(thumbUrl || `https://picsum.photos/seed/${item.id}/160/160`)}" alt="" loading="lazy">
        ${isVideo ? `<span class="history-thumb-play"><i class="bi bi-play-fill"></i></span>` : ''}
      </span>`;
  }

  // Estado (texto + icono o spinner)
  const isPendingStatus = item.status === 'processing' || item.status === 'pending' || item.status === 'queued';
  const statusHtml = isPendingStatus
    ? `<span class="history-status ${status.cls}"><span class="history-spinner"></span> ${esc(status.label)}</span>`
    : `<span class="history-status ${status.cls}"><i class="bi ${status.icon || 'bi-check-circle-fill'}"></i> ${esc(status.label)}</span>`;

  // Meta extra (modelo, calidad, etc.) y nº de resultados
  const metaExtras = [];
  if (item.meta) {
    if (typeof item.meta === 'object') {
      const parts = [];
      if (item.model) parts.push(item.model);
      if (item.meta.aspect) parts.push(item.meta.aspect);
      if (item.meta.quality) parts.push(item.meta.quality.toUpperCase());
      if (item.meta.duration) parts.push(`${item.meta.duration}s`);
      if (item.meta.voiceId) parts.push(`Voz: ${item.meta.voiceId}`);
      if (parts.length > 0) {
        metaExtras.push(`<span>${esc(parts.join(' · '))}</span>`);
      }
    } else {
      metaExtras.push(`<span>${esc(item.meta)}</span>`);
    }
  }
  if (item.resultsCount) metaExtras.push(`<span>${item.resultsCount} resultados</span>`);

  a.innerHTML = `
    ${thumbHtml}
    <span class="history-body">
      <span class="history-type-badge history-type-${item.type}">
        <i class="bi ${cfg.icon}"></i> ${esc(cfg.label)}
      </span>
      <span class="history-title">${esc(item.title || 'Sin título')}</span>
      <span class="history-meta">
        ${statusHtml}
        <span class="history-dot">·</span>
        <time datetime="${esc(item.createdAt || '')}">${formatTime(item.createdAt)}</time>
        ${metaExtras.map(m => `<span class="history-dot">·</span>${m}`).join('')}
      </span>
    </span>
    <i class="bi bi-chevron-right history-arrow"></i>
  `;
  return a;
}

// Tiempo relativo "hace X horas" / "ayer" / "hace 3 días"
function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 60)        return 'hace unos segundos';
  if (diffSec < 3600)      return `hace ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400)     return `hace ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 172800)    return 'ayer';
  if (diffSec < 604800)    return `hace ${Math.floor(diffSec / 86400)} días`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Integración con backend (opcional) ────────────────────────
async function fetchFromBackend() {
  const list = qs('#historialList');
  try {
    const items = await listHistory();
    if (Array.isArray(items)) {
      setItems(items);
    } else {
      setItems([]);
    }
    return items;
  } catch (err) {
    console.error('[Vimo] error al cargar historial:', err);
    if (list) list.innerHTML = '';
    qs('#historialEmpty')?.classList.remove('is-hidden');
    return [];
  }
}
